import React, { Children, Fragment, PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import {
	TooltipRoot, TooltipRowLabel, TooltipRowValue, TooltipTitle, tooltipMotionMs, tooltipTipOffset
} from './Tooltip.styled';
import { TriggerType, usePopperTooltip } from 'react-popper-tooltip';
import * as PopperJS from '@popperjs/core';

export type TooltipRow = {
	label: ReactNode;
	/** Usually a link — it is styled as one. */
	value: ReactNode;
}

type Props = {
	/** One label. The plain chip. Shown as written. */
	message?: string;
	/** Structured content instead of a label: a label column and a value column,
	 *  aligned down the panel. Wins over `message` when both are given. */
	rows?: TooltipRow[];
	/** Heading above `rows`, on the other side of a rule. */
	title?: ReactNode;
	/** Free-form content on a surface card instead of the chip — a hover card. Wins over
	 *  `rows` and `message`. The content owns its own padding and width. */
	content?: ReactNode;
	placement?: PopperJS.Placement;
	delayHide?: number;
	delayShow?: number;
	/** What opens the tooltip. `'click'` keeps it open until a click outside. */
	trigger?: TriggerType | TriggerType[];
	/** Open on mount — for showcases and tests, not for app code. */
	defaultVisible?: boolean;
	/** Uppercase the label. Off by default: a tooltip usually carries a sentence,
	 *  and uppercasing one costs the word shapes the eye reads it by. */
	uppercase?: boolean;
}

export function Tooltip(
	{
		message,
		rows,
		title,
		content,
		children,
		placement = 'auto',
		delayHide = 100,
		delayShow = 0,
		trigger = 'hover',
		defaultVisible,
		uppercase,
	}: PropsWithChildren<Props>
) {

	const {
		getArrowProps,
		getTooltipProps,
		setTooltipRef,
		setTriggerRef,
		visible,
	} = usePopperTooltip({
		interactive: true,
		offset: [0, tooltipTipOffset],
		delayHide,
		delayShow,
		placement,
		trigger,
		defaultVisible,
	}, {
		// The tooltip renders next to its trigger, so an absolutely positioned one
		// is clipped by any scrolling ancestor — a nav or a menu list shows only
		// the sliver that fits inside the box. Fixed is measured against the
		// viewport instead.
		// ponytail: fixed escapes `overflow`, but not an ancestor that establishes
		// a containing block (transform, filter, will-change). Portal the panel if
		// one ever does.
		strategy: 'fixed',
	});

	// The tooltip has to outlive `visible` so its exit can play. A timer rather
	// than animationend, matching Dropdown: the event never arrives when the
	// animation is disabled or the tab is backgrounded, and a tooltip that never
	// unmounts is worse than one that unmounts a frame late.
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		if (visible) {
			setMounted(true);
			return;
		}
		const gone = setTimeout(() => setMounted(false), tooltipMotionMs);

		return () => clearTimeout(gone);
	}, [visible]);

	const structured = !!rows?.length;

	if (!message && !structured && !content) {
		return <>{children}</>;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const child = Children.only(children) as React.ReactElement & { ref?: React.Ref<any> };

	return (
		<>
			{React.cloneElement(child, { ref: setTriggerRef })}
			{mounted && (
				<TooltipRoot
					$exiting={!visible}
					$rich={structured}
					$card={!!content}
					$uppercase={uppercase}
					ref={setTooltipRef}
					{...getTooltipProps({ className: 'tooltip-container' })}>
					{content ?? (structured ? <>
						{title && <TooltipTitle>{title}</TooltipTitle>}
						{rows?.map(({ label, value }, i) => (
							<Fragment key={i}>
								<TooltipRowLabel>{label}</TooltipRowLabel>
								<TooltipRowValue>{value}</TooltipRowValue>
							</Fragment>
						))}
					</> : message)}
					<div {...getArrowProps({ className: 'tooltip-arrow' })} />
				</TooltipRoot>
			)}
		</>
	);
}

